import React, { useState } from 'react';
import { color, naira, radius, space } from './ui/theme';
import { Banner, Btn } from './ui/kit';

/**
 * Shown when a customer being entered looks like one that already exists.
 *
 * 108 records held 18 duplicates of each other because nothing ever looked at
 * the point of entry. Afterwards the duplicate carries orders, and combining
 * them is a merge: permanent, and somebody's afternoon.
 *
 * It ASKS rather than blocks. Two records on one phone number are often two
 * real customers -- a hospital and the nurse who orders for it, a shared
 * office line. A hard block teaches people to mistype a digit to get past it,
 * which produces the same duplicate with worse data. Saying "this is a
 * different customer" is allowed, needs a reason, and is kept on the record
 * with the name of whoever said it.
 */

const inputStyle = {
  width: '100%', padding: '9px 11px', borderRadius: radius.sm,
  border: `1px solid ${color.border}`, fontSize: 13.5,
};

export default function DuplicateCustomerDialog({
  entered, matches, onUseExisting, onCreateAnyway, onCancel, busy,
}) {
  const [reason, setReason] = useState('');

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 680, width: '95%' }}
           onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>You may already have this customer</h3>
          <Btn size="sm" variant="secondary" onClick={onCancel}>Cancel</Btn>
        </div>

        <div className="modal-body">
          <div style={{ fontSize: 13, marginBottom: space(1.5) }}>
            You are adding <strong>{entered?.name}</strong>
            {entered?.phone && <> &middot; {entered.phone}</>}.
            {' '}{matches.length === 1 ? 'This record' : `These ${matches.length} records`}
            {' '}already {matches.length === 1 ? 'exists' : 'exist'}:
          </div>

          <div style={{ display: 'grid', gap: space(1),
                        marginBottom: space(2) }}>
            {matches.map(m => (
              <div key={m.customer_id}
                   style={{ border: `1px solid ${color.border}`,
                            borderRadius: radius.sm, padding: space(1.25) }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                              gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <strong style={{ fontSize: 13.5 }}>{m.name}</strong>
                    <div style={{ fontSize: 12, color: color.textSecondary }}>
                      {m.phone || 'no phone'}
                      {m.customer_code && <> &middot; {m.customer_code}</>}
                    </div>
                    <div style={{ fontSize: 11.5, color: color.warning || '#b26a00',
                                  marginTop: 3 }}>
                      {m.reasons.join(', ')}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12 }}>
                    <div><strong>{m.orders}</strong> order{m.orders === 1 ? '' : 's'}</div>
                    <div style={{ color: color.textSecondary }}>{naira(m.value)}</div>
                    {m.last_order && (
                      <div style={{ fontSize: 11, color: color.textMuted }}>
                        last {m.last_order.slice(0, 10)}
                      </div>)}
                  </div>
                </div>
                <div style={{ marginTop: space(1) }}>
                  <Btn size="sm" disabled={busy}
                       onClick={() => onUseExisting(m)}>
                    Use this customer instead
                  </Btn>
                </div>
              </div>
            ))}
          </div>

          <Banner tone="warning" title="If it really is a different customer">
            Say why. It is kept on the record with your name, so the next
            person who sees these two together already has the answer.
          </Banner>

          <div style={{ marginTop: space(1) }}>
            <input style={inputStyle} value={reason} autoFocus
                   placeholder="e.g. different branch, same owner"
                   onChange={(e) => setReason(e.target.value)} />
          </div>

          <div style={{ marginTop: space(1.5), display: 'flex', gap: 8,
                        flexWrap: 'wrap' }}>
            <Btn variant="secondary" disabled={busy}
                 onClick={() => {
                   if (reason.trim().length < 3) return;
                   onCreateAnyway(reason.trim());
                 }}>
              {busy ? 'Saving…' : 'This is a different customer — add it'}
            </Btn>
            <Btn variant="secondary" disabled={busy} onClick={onCancel}>
              Go back and edit
            </Btn>
          </div>
          {reason.trim().length > 0 && reason.trim().length < 3 && (
            <div style={{ fontSize: 11.5, color: color.danger,
                          marginTop: 6 }}>
              Give a reason of at least three characters.
            </div>)}
        </div>
      </div>
    </div>
  );
}
